import countBy from "lodash/countBy";
import sortBy from "lodash/sortBy";
import { inject, injectable } from "inversify";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { webScriptBackend } from "./webScriptBackend";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";

@injectable()
export class ConversationFilterService {
  private logger: FrontLogger;
  isInitialized = false;
  constructor(
    @inject(IInstantMessagingServiceSymbol) private readonly instantMessagingService: InstantMessagingService,
    @inject(FrontLogger) private readonly frontLogger: FrontLogger
  ) {
    this.logger = this.frontLogger.getLogger("conversation-filter-service");
  }

  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    webScriptBackend
      .on(WebScriptCommands.CHANGE_VISIBLE_CONTACTS, async ({ filter, refreshStrategy }) => {
        await this.instantMessagingService.filterContactList({
          filter,
          refreshStrategy,
          refreshOnEvent: false,
        });
      })
      .on(WebScriptCommands.SHOW_FILTERED_CHATS_AMOUNT, () => this.instantMessagingService.getFilteredChatIds())
      .on(WebScriptCommands.COUNT_FILTER_WITHOUT_APPLYING, (filter) => {
        return this.countFilterWithoutApplying(filter);
      })
      .on(WebScriptCommands.GET_CONTACTS_COUNTRY_CODES, () => this.getCountryCodes())
      .on(WebScriptCommands.INSTANT_MESSAGING_GET_CHATS_LABELS, async () => {
        return (await this.instantMessagingService.getChatsLabels?.()) || new Map();
      })
      .on(WebScriptCommands.INSTANT_MESSAGING_ADD_LABEL, async (labelName) => {
        return await this.instantMessagingService.addLabel?.(labelName);
      })
      .on(WebScriptCommands.INSTANT_MESSAGING_DELETE_LABEL, async (labelId) => {
        return (await this.instantMessagingService.deleteLabel?.(labelId)) || false;
      })
      .on(WebScriptCommands.GET_SUPPORTED_FILTERS, () => this.instantMessagingService.supportedFilters?.slice())
      .on(WebScriptCommands.GET_BUSINESS_LABELS, () => this.getBusinessLabels())
      .on(WebScriptCommands.GET_IS_WHATSAPP_BUSINESS, () => this.instantMessagingService.isBusiness?.() ?? false);
  }

  async getCountryCodes() {
    try {
      const codes = await this.instantMessagingService.getContactCountryCodes();
      const countryCodes = sortBy(Object.entries(countBy(codes)), ([, count]) => count)
        .map(([countryCode, count]) => ({ countryCode, count }))
        .reverse();
      return countryCodes;
    } catch (e) {
      this.logger.error({ err: e as Error }, "Error getting country codes");
    }
  }

  async getBusinessLabels() {
    try {
      const labels = await this.instantMessagingService.getBusinessLabels?.();
      return sortBy(labels ?? [], "count").reverse();
    } catch (e) {
      this.logger.error({ err: e as Error }, "Error getting buisiness labels");
    }
  }

  async countFilterWithoutApplying(filter: any): Promise<number> {
    try {
      const count = await this.instantMessagingService.countFilteredChats?.(filter);

      if (typeof count === "number") {
        return count;
      }

      this.logger.warn("countFilteredChats not implemented for this service");
      return 0;
    } catch (e) {
      this.logger.error({ err: e as Error }, "Error counting filter without applying");
      return 0;
    }
  }
}
